import { corridorKey } from '../corridorKey.js'
import { ALL_MODES } from '../filters/modeFilter.js'

/**
 * The ranked corridor list's data model (spec §8).
 *
 * §8.1: cross-modal *checking* is solved by the inspector, but cross-modal
 * *scanning* — "find pairs where one mode is starved and another is jammed" —
 * cannot be done by eye on coloured lines at any arrangement. It is a comparison
 * across a derived quantity, and this module is that quantity.
 *
 * Joined entirely on the client: `corridorState` and `corridorDemand` both derive
 * one row per `RouteDemand` row and both refresh on the same tick, so the join is
 * 1:1 by construction. `baseDailyDemandUnits` already ships to the client, so
 * ranking on it discloses nothing new.
 *
 * Pure module: no Vue, no Leaflet, no store.
 */

/**
 * Saturation bucket → how much a unit of demand on it is worth looking at.
 *
 * **This is the demand *weighting* §8.3 requires, not a tiebreak.** Sorting
 * bucket-first and demand-second would put every undersupplied corridor above
 * every balanced one — including a 3 unit/day starved regional corridor above a
 * 200 unit/day balanced trunk, which is precisely the failure §8.3 names:
 * "starved regional corridors carrying trivial demand — technically
 * undersupplied, not worth a vehicle."
 *
 * A weighted product lets a big balanced market outrank a tiny empty one while
 * still favouring emptiness at comparable size. §8.4 treats the panel as a design
 * experiment whose result is input to the economy work, so these three numbers
 * are the intended tuning surface — change them here and the whole ranking moves.
 */
export const SATURATION_WEIGHT = { UNDERSUPPLIED: 1.0, BALANCED: 0.5, SATURATED: 0.2 }

/** Committed capacity, ascending — less capacity already piling in ranks higher. */
const COMMITTED_ORDER = { NONE: 0, LOW: 1, MEDIUM: 2, HIGH: 3 }

/**
 * How many rows the panel renders. `RouteDemandSeeder` produces intra-zone
 * all-pairs directional × shared modes plus inter-zone pairs × modes over 87
 * nodes — plausibly thousands. A ranked list's value is at the top (§8.1), a
 * rail column cannot show thousands of rows, and a fixed cap keeps scroll
 * restoration trivial and virtualization unnecessary.
 */
export const RANKED_ROW_LIMIT = 50

/** Sort key → comparator input. `opportunity` is the default and the point of the panel. */
export const DEFAULT_SORT = { key: 'opportunity', dir: 'desc' }

/**
 * Joins bucketed corridor state onto corridor demand.
 *
 * Corridors with no state row keep their demand and fall back to the neutral
 * buckets, so a corridor never silently disappears from the list because one of
 * the two endpoints of the join lagged.
 *
 * @param {{corridorDemand: Array, corridorState: Array}} sources
 * @returns {Array<Object>} one row per corridor, each carrying its key and score
 */
export function joinCorridorRows({ corridorDemand, corridorState }) {
  const stateByKey = new Map()
  for (const state of corridorState ?? []) {
    const key = corridorKey(state)
    if (key) stateByKey.set(key, state)
  }

  const rows = []
  for (const demand of corridorDemand ?? []) {
    const key = corridorKey(demand)
    if (!key) continue

    const state = stateByKey.get(key)
    const row = {
      key,
      originNodeId:      demand.originNodeId,
      destinationNodeId: demand.destinationNodeId,
      originName:        demand.originNodeName ?? demand.originName ?? null,
      destinationName:   demand.destinationNodeName ?? demand.destinationName ?? null,
      mode:              demand.mode,
      demandUnits:       Number(demand.baseDailyDemandUnits ?? 0),
      saturation:        state?.saturation ?? 'BALANCED',
      committedCapacity: state?.committedCapacity ?? 'NONE',
    }
    row.opportunity = opportunityScore(row)
    rows.push(row)
  }
  return rows
}

/**
 * §8.3's "opportunity", as one number.
 *
 * @param {{saturation: string, demandUnits: number}} row
 * @returns {number}
 */
export function opportunityScore(row) {
  return (SATURATION_WEIGHT[row.saturation] ?? SATURATION_WEIGHT.BALANCED) * (row.demandUnits || 0)
}

/**
 * The panel's default mode set — **all four**, deliberately not `modeFilter.js`'s
 * `DEFAULT_MODES`.
 *
 * The map defaults to a single mode because §5.1 needs one line per corridor for
 * unambiguous hit-testing. A table has no such constraint, and §8.1's entire
 * value proposition is cross-modal scanning: "find pairs where one mode is
 * starved and another is jammed." Defaulting this list to ROAD-only would remove
 * the one thing the panel exists to do.
 */
export const DEFAULT_PANEL_MODES = [...ALL_MODES]

/**
 * Coerces a stored or supplied value into a valid panel mode set. Nullish, junk
 * and empty all fall back to every mode — never to a single one.
 *
 * @param {string[]|null|undefined} value
 * @returns {string[]} canonically ordered, never empty
 */
export function normalizePanelModes(value) {
  if (!Array.isArray(value)) return [...ALL_MODES]
  const kept = ALL_MODES.filter(m => value.includes(m))
  return kept.length > 0 ? kept : [...ALL_MODES]
}

/** Filters rows to an active mode set. */
export function filterRows(rows, modes) {
  const active = normalizePanelModes(modes)
  if (active.length === ALL_MODES.length) return rows
  return rows.filter(row => active.includes(row.mode))
}

const COMPARATORS = {
  opportunity: (a, b) => a.opportunity - b.opportunity,
  demand:      (a, b) => a.demandUnits - b.demandUnits,
  saturation:  (a, b) => (SATURATION_WEIGHT[a.saturation] ?? 0) - (SATURATION_WEIGHT[b.saturation] ?? 0),
  committed:   (a, b) => (COMMITTED_ORDER[a.committedCapacity] ?? 0) - (COMMITTED_ORDER[b.committedCapacity] ?? 0),
  corridor:    (a, b) => `${a.originName}${a.destinationName}`.localeCompare(`${b.originName}${b.destinationName}`),
}

/**
 * Sorts rows, then breaks ties deterministically.
 *
 * The tiebreaks are load-bearing, not tidiness. Buckets are coarse, so ties are
 * common; without a stable final key the table would visibly reshuffle every tick
 * as the array order changed underneath an equal-score comparison, and a list
 * that rearranges itself while being read is unusable.
 *
 * Committed capacity ascending comes first among the tiebreaks — it is §8.3's
 * herding mitigation ("the corridor that ranks highest on trailing data while
 * capacity visibly piles in is legible as a trap"), which belongs as a tiebreak
 * rather than as a primary signal.
 *
 * @param {Array} rows
 * @param {{key: string, dir: 'asc'|'desc'}} sort
 * @returns {Array} a new array
 */
export function sortRows(rows, sort = DEFAULT_SORT) {
  const compare = COMPARATORS[sort?.key] ?? COMPARATORS.opportunity
  const sign = sort?.dir === 'asc' ? 1 : -1

  return [...rows].sort((a, b) => {
    const primary = compare(a, b) * sign
    if (primary !== 0) return primary

    const committed = (COMMITTED_ORDER[a.committedCapacity] ?? 0) - (COMMITTED_ORDER[b.committedCapacity] ?? 0)
    if (committed !== 0) return committed

    return a.key.localeCompare(b.key)
  })
}

/**
 * The rows the panel actually renders, and how many there were before the cap.
 *
 * The cap is applied *after* ranking, so it always keeps the most interesting
 * rows — slicing first would cap an arbitrary set and then order it.
 *
 * @returns {{rows: Array, total: number, truncated: boolean}}
 */
export function rankedRows({ corridorDemand, corridorState }, { modes, sort } = {}) {
  const filtered = filterRows(joinCorridorRows({ corridorDemand, corridorState }), modes)
  const sorted = sortRows(filtered, sort ?? DEFAULT_SORT)

  return {
    rows: sorted.slice(0, RANKED_ROW_LIMIT),
    total: sorted.length,
    truncated: sorted.length > RANKED_ROW_LIMIT,
  }
}
